import { useEffect, useState } from "react";
import api from "@/lib/api";
import { siteUrl } from "@/lib/sites";

/** The admin's slides for one site ("home", "shop", "care", "community"),
 *  ready for BannerSlider. Each slide's link opens on the site it belongs to:
 *  a router link on this site, a full link to another PawBazaar site.
 *  `mediaUrl` turns stored picture paths into addresses (each site has its own). */

// Where each kind of link opens (backend routes/banners.py link types).
const LINKS = {
  product: (v) => ["shop", `/product/${v}`],
  category: (v) => ["shop", `/shop?category=${encodeURIComponent(v)}`],
  shop: (v) => ["shop", `/vendor/${v}`],
  search: (v) => ["shop", `/shop?q=${encodeURIComponent(v)}`],
  coupon: (v) => ["shop", `/cart?coupon=${encodeURIComponent(v)}`],
  care_service: (v) => ["care", `/service/${v}`],
  care_provider: (v) => ["care", `/services/${v}`],
  care_package: (v) => ["care", `/services/${v}?tab=packages`],
  ngo: (v) => ["community", `/ngos/${v}`],
  adopt_pet: (v) => ["community", `/adopt/${v}`],
  drive: (v) => ["community", `/drives/${v}`],
  campaign: (v) => ["community", `/campaigns/${v}`],
};

export function slideLink(b, current) {
  if (b.link_type === "url") return { href: b.link_value };
  const make = LINKS[b.link_type];
  if (!make) return {};
  const [site, path] = make(b.link_type === "coupon" ? (b.coupon_code || b.link_value || "") : (b.link_value || ""));
  return site === current ? { to: path } : { href: `${siteUrl(site)}${path}` };
}

export function toSlide(b, current, mediaUrl) {
  return {
    id: b.id, title: b.title, subtitle: b.subtitle, eyebrow: b.eyebrow, cta: b.cta_label,
    image: mediaUrl(b.image_url), imagePhone: b.image_phone_url ? mediaUrl(b.image_phone_url) : null,
    focus: [b.focus_x ?? 50, b.focus_y ?? 50], tone: b.tone || "teal", ...slideLink(b, current),
  };
}

/** [slides, loaded]: the live slides for `site` (shown on the site `current`). */
export function useSiteSlides(site, current, mediaUrl) {
  const [slides, setSlides] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/banners", { params: { site } })
      .then(({ data }) => alive && setSlides((Array.isArray(data) ? data : []).map((b) => toSlide(b, current, mediaUrl))), () => alive && setSlides([]))
      .finally(() => alive && setLoaded(true));
    return () => { alive = false; };
    // mediaUrl is a plain module function; the slides only change with the site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, current]);
  return [slides, loaded];
}
